// src/components/PurchaseFlow/Steps/DocumentUpload/DocumentUploadStep.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faMobileScreen } from '@fortawesome/free-solid-svg-icons';
import DocumentItem from '../../../DocumentItem/PendingDocumentItem';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../../services/api';
import { TokenService } from '../../../../services/tokenService';
import { trackDocumentUploaded } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import {
  loadContractFromEnv,
  buildInitialDocumentsMap,
  buildDocumentFlowPayload,
  getDocumentMetaForKey,
  groupDocumentsBySection,
  hasOptionalDocs,
  resolveDocumentLabel,
  sectionHasRequiredDocs,
} from '../../../../modules/documents';
import './DocumentUploadStep.css';

const MySwal = withReactContent(Swal);

const DocumentUploadStep = ({ onNext, onBack, clientData, purchaseRequestId }) => {
  const contract = useMemo(() => loadContractFromEnv(process.env), []);
  const [documents, setDocuments] = useState(() => buildInitialDocumentsMap(contract));
  const [sectionSkipFlags, setSectionSkipFlags] = useState({});
  const [optionalOpen, setOptionalOpen] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [qrUrl, setQrUrl] = useState(null);
  const [pollingTimeout, setPollingTimeout] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const { commerceId, branchId } = useAnalyticsContext();

  const allowDeferredDocumentation = contract.allowDeferredDocumentation;

  useEffect(() => {
    const generateTemporaryToken = async () => {
      try {
        const response = await TokenService.generateOnboardingToken(
          clientData.id,
          purchaseRequestId,
          30
        );

        const { token } = response.data;
        const mobileUrl = TokenService.generateMobileUrl(token, 'onboarding');
        setQrUrl(mobileUrl);

        if (mobileUrl) {
          QRCode.toCanvas(document.getElementById('qr-canvas'), mobileUrl, {
            width: 150,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff',
            },
          });
        }
      } catch (error) {
        console.error('Error al generar token temporal:', error);
        MySwal.fire({
          title: 'Error',
          text: 'No se pudo generar el acceso móvil',
          icon: 'error',
        });
      }
    };

    generateTemporaryToken();
  }, [clientData.id, purchaseRequestId]);

  const handleFileUpload = (effectiveKey, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setDocuments((prevDocs) => ({
        ...prevDocs,
        [effectiveKey]: {
          ...prevDocs[effectiveKey],
          file,
          preview: reader.result,
          status: 'selected',
          uploadedFromMobile: false,
        },
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleReject = (effectiveKey) => {
    setDocuments((prevDocs) => ({
      ...prevDocs,
      [effectiveKey]: {
        ...prevDocs[effectiveKey],
        file: null,
        preview: null,
        status: 'pending',
      },
    }));
  };

  const allRequiredSatisfied = () => {
    for (const section of contract.sections) {
      if (sectionSkipFlags[section.key]) continue;
      for (const doc of section.documents) {
        if (doc.requirementLevel !== 'required') continue;
        const d = documents[doc.effectiveCategoryAlias];
        if (!d) return false;
        if (d.status !== 'uploaded' && d.status !== 'selected') return false;
      }
    }
    return true;
  };

  const getDeferrableDisplayNames = () => {
    const names = [];
    contract.sections.forEach((section) => {
      if (sectionSkipFlags[section.key]) return;
      section.documents.forEach((doc) => {
        if (doc.requirementLevel !== 'required') return;
        const d = documents[doc.effectiveCategoryAlias];
        if (d && d.status === 'pending') {
          const meta = getDocumentMetaForKey(doc.effectiveCategoryAlias);
          names.push(meta.name);
        }
      });
    });
    return names;
  };

  const startPolling = useCallback(() => {
    const timeout = setTimeout(() => {
      const checkDocuments = async () => {
        try {
          const response = await api.get(
            `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/documents`
          );

          setDocuments((prevDocuments) => {
            const updatedDocuments = { ...prevDocuments };
            const newDocuments = [];

            response.data.forEach((serverDoc) => {
              if (updatedDocuments[serverDoc.type]) {
                const currentDoc = updatedDocuments[serverDoc.type];

                if (
                  serverDoc.status === 'uploaded' &&
                  (currentDoc.status === 'pending' || currentDoc.status === 'selected')
                ) {
                  updatedDocuments[serverDoc.type] = {
                    ...currentDoc,
                    status: 'uploaded',
                    preview: serverDoc.file.url,
                    uploadedFromMobile: true,
                    title: serverDoc.title,
                    file: null,
                  };

                  if (commerceId) {
                    trackDocumentUploaded({
                      commerceId,
                      branchId,
                      documentCategory: serverDoc.type,
                      customerDni: clientData?.dni,
                      uploadedFromMobile: true,
                    });
                  }

                  const meta = getDocumentMetaForKey(serverDoc.type);
                  newDocuments.push(meta.name);
                }
              }
            });

            if (newDocuments.length > 0) {
              MySwal.fire({
                icon: 'success',
                title: '¡Nuevos documentos!',
                text: `El cliente ha subido: ${newDocuments.join(', ')}`,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
              });
            }

            return newDocuments.length > 0 ? updatedDocuments : prevDocuments;
          });
        } catch (error) {
          console.error('Error verificando documentos:', error);
        }
      };

      checkDocuments();
      const interval = setInterval(checkDocuments, 10000);
      setPollingInterval(interval);
    }, 1);

    setPollingTimeout(timeout);
  }, [clientData.id, purchaseRequestId, commerceId, branchId, clientData?.dni]);

  useEffect(() => {
    startPolling();
  }, [startPolling]);

  useEffect(() => {
    return () => {
      if (pollingTimeout) clearTimeout(pollingTimeout);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingTimeout, pollingInterval]);

  useEffect(() => {
    if (qrUrl) {
      QRCode.toCanvas(document.getElementById('qr-canvas'), qrUrl, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
    }
  }, [qrUrl]);

  const emitNext = (payload) => {
    onNext({
      documentFlowState: payload,
    });
  };

  const handleNext = async () => {
    if (!allRequiredSatisfied()) {
      const deferrableList = getDeferrableDisplayNames();

      MySwal.fire({
        title: 'Documentos requeridos pendientes',
        html: `
          <div class="text-start">
            <p class="mb-3 text-muted">Los siguientes documentos son requeridos para continuar:</p>
            <ul class="list-unstyled">
              ${deferrableList
                .map(
                  (doc) =>
                    `<li class="mb-2 d-flex align-items-center"><i class="bi bi-exclamation-triangle text-warning me-2"></i><span>${doc}</span></li>`
                )
                .join('')}
            </ul>
            ${
              allowDeferredDocumentation
                ? `<div class="border-top pt-3 mt-3">
                <div class="d-flex align-items-start">
                  <i class="bi bi-lightbulb text-primary me-2 mt-1 flex-shrink-0"></i>
                  <div class="text-muted small">
                    Si deseas proceder sin estos documentos, puedes usar el botón <strong>"Cargar después"</strong> para diferir la documentación.
                  </div>
                </div>
              </div>`
                : `<div class="border-top pt-3 mt-3">
                <div class="d-flex align-items-start">
                  <i class="bi bi-info-circle text-warning me-2 mt-1 flex-shrink-0"></i>
                  <div class="text-muted small">
                    <strong>Importante:</strong> Todos los documentos requeridos deben ser subidos para continuar.
                  </div>
                </div>
              </div>`
            }
          </div>
        `,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#6f42c1',
        customClass: {
          popup: 'swal2-popup-custom',
        },
      });
      return;
    }

    const pendingUploads = Object.entries(documents)
      .filter(([_, doc]) => doc.status === 'selected')
      .map(([key]) => key);

    if (pendingUploads.length === 0) {
      emitNext(buildDocumentFlowPayload(documents, contract, sectionSkipFlags));
      return;
    }

    try {
      setIsUploading(true);
      setProgress(0);

      const formData = new FormData();
      pendingUploads.forEach((docType) => {
        const doc = documents[docType];
        formData.append('files[]', doc.file);
        formData.append('categories[]', docType);
      });

      const response = await api.post(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (event) => {
            const progressValue = Math.round((event.loaded / event.total) * 100);
            setProgress(progressValue);
          },
        }
      );

      const nextDocuments = { ...documents };
      pendingUploads.forEach((docType) => {
        if (nextDocuments[docType]) {
          nextDocuments[docType] = {
            ...nextDocuments[docType],
            status: 'uploaded',
            file: null,
          };
        }
      });
      setDocuments(nextDocuments);

      if (commerceId && response.data) {
        pendingUploads.forEach((docType) => {
          trackDocumentUploaded({
            commerceId,
            branchId,
            documentCategory: docType,
            customerDni: clientData?.dni,
            uploadedFromMobile: false,
          });
        });
      }

      emitNext(buildDocumentFlowPayload(nextDocuments, contract, sectionSkipFlags));
    } catch (error) {
      console.error('Error uploading documents:', error);
      setErrorMessage('Hubo un problema al cargar los documentos. Por favor, vuelve a intentarlo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkipDocumentation = () => {
    const deferrableList = getDeferrableDisplayNames();

    if (deferrableList.length === 0) {
      emitNext(buildDocumentFlowPayload(documents, contract, sectionSkipFlags));
      return;
    }

    MySwal.fire({
      title: '¿Continuar sin documentación?',
      html: `
        <div class="text-start">
          <p class="mb-3 text-muted">Los siguientes documentos quedarán pendientes:</p>
          <ul class="list-unstyled">
            ${deferrableList
              .map(
                (doc) =>
                  `<li class="mb-2 d-flex align-items-center"><i class="bi bi-exclamation-triangle text-warning me-2"></i><span>${doc}</span></li>`
              )
              .join('')}
          </ul>
          <div class="border-top pt-3 mt-3">
            <div class="d-flex align-items-start">
              <i class="bi bi-info-circle text-warning me-2 mt-1 flex-shrink-0"></i>
              <div class="text-muted small">
                <strong>Importante:</strong> Esta documentación deberá ser subida posteriormente para completar y validar la venta.
              </div>
            </div>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#435ebe',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-secondary',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        emitNext(buildDocumentFlowPayload(documents, contract, sectionSkipFlags));
      }
    });
  };

  const handleShareMobileAccess = () => {
    if (!qrUrl) return;

    MySwal.fire({
      title: 'Subir documentos desde el celular',
      html: `
        <div class="text-center">
          <canvas id="modal-qr-canvas" class="mb-4"></canvas>
          <p class="mb-3">El cliente puede escanear este código QR para subir los documentos desde su celular.</p>
          
          <div class="alert alert-warning d-flex align-items-center gap-2 mb-4" style="font-size: 0.9rem;">
            <div class="container-fluid">
              <div class="d-flex align-items-center gap-2">
                <i class="bi bi-exclamation-triangle-fill"></i>
                <div class="text-start">
                  Este link expirará en 30 minutos.
                </div>
              </div>
            </div>
          </div>

          <hr />
          <div class="row g-3">
            <div class="col-6">
              <p class="text-muted small mb-2">Copia este enlace:</p>
              <div class="input-group">
                <input 
                  type="text" 
                  value="${qrUrl}" 
                  class="form-control form-control-sm"
                  readonly
                />
                <button 
                  class="btn btn-outline-secondary copy-button"
                  type="button"
                  style="border: 1px solid #dce7f1 !important"
                  onclick="copyToClipboard('${qrUrl}', this)"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </div>
            </div>
            <div class="col-6">
              <p class="text-muted small mb-2">O comparte con el cliente:</p>
              <a 
                href="https://wa.me/?text=${encodeURIComponent(
                  `Puedes subir tus documentos desde aquí: ${qrUrl}`
                )}" 
                target="_blank" 
                class="btn btn-success w-100"
                style="background-color: #25D366; border-color: #25D366"
              >
                <i class="bi bi-whatsapp me-2"></i>
                Enviar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: () => {
        QRCode.toCanvas(document.getElementById('modal-qr-canvas'), qrUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
        });

        window.copyToClipboard = (text, button) => {
          navigator.clipboard.writeText(text).then(() => {
            const originalContent = button.innerHTML;
            button.innerHTML = '<i class="bi bi-check"></i>';
            button.disabled = true;
            setTimeout(() => {
              button.innerHTML = originalContent;
              button.disabled = false;
            }, 2000);
          });
        };
      },
    });
  };

  const handleMarkGuarantorNotRequired = (sectionKey) => {
    MySwal.fire({
      title: '¿Confirmar?',
      text: 'Se registrará que esta venta no requiere documentación de garante.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        setSectionSkipFlags((prev) => ({ ...prev, [sectionKey]: true }));
      }
    });
  };

  const renderDocumentItem = (effectiveKey, doc) => {
    if (!doc) {
      return null;
    }
    const meta = getDocumentMetaForKey(effectiveKey);
    return (
      <DocumentItem
        key={effectiveKey}
        icon={meta.icon || faFile}
        name={meta.name}
        status={doc.status}
        onFileUpload={(file) => handleFileUpload(effectiveKey, file)}
        filePreview={doc.preview}
        onReject={() => handleReject(effectiveKey)}
        isUploading={isUploading}
        uploadedFromMobile={doc.uploadedFromMobile}
        onPreviewClick={() => {
          if (doc.preview) {
            MySwal.fire({
              title: resolveDocumentLabel({ type: effectiveKey, title: doc.title }),
              imageUrl: doc.preview,
              imageAlt: 'Vista previa del documento',
              width: '80%',
              confirmButtonText: 'Cerrar',
            });
          }
        }}
      />
    );
  };

  const grouped = groupDocumentsBySection(contract, documents);

  return (
    <div id="documentUploadStep" className="container my-5">
      <div className="position-relative mb-4">
        <h4 className="text-center mb-0">Carga de Documentos</h4>
        {allowDeferredDocumentation && getDeferrableDisplayNames().length > 0 && (
          <button
            className="btn btn-link text-muted position-absolute end-0 top-50 translate-middle-y"
            onClick={handleSkipDocumentation}
            disabled={isUploading}
            type="button"
          >
            <i className="bi bi-arrow-right ms-1"></i> Completar luego
          </button>
        )}
      </div>

      <div className="row justify-content-center align-items-start">
        <div className="col-md-8">
          <h5 className="mb-2">Desde tu computadora</h5>
          <p className="text-muted small mb-2">
            Arrastra y suelta los documentos en las áreas correspondientes.
          </p>

          <div className="d-flex flex-column gap-1">
          {grouped.map((section) => (
            <div key={section.key} className="card document-section-card shadow-sm mb-0">
              <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="fw-semibold">{section.label}</span>
                {section.allowSkip && section.key.startsWith('guarantor') && !sectionSkipFlags[section.key] && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleMarkGuarantorNotRequired(section.key)}
                    disabled={isUploading}
                  >
                    Esta venta no requiere documentación de garante.
                  </button>
                )}
              </div>
              <div className="card-body py-2">
                {sectionSkipFlags[section.key] && section.allowSkip && (
                  <div className="alert alert-light border mb-3" role="status">
                    Esta venta no requiere documentación de garante.
                  </div>
                )}

                {!sectionSkipFlags[section.key] && (
                  <>
                    {sectionHasRequiredDocs(section) && (
                      <>
                        <h6 className="text-muted small text-uppercase mb-2">Requeridos</h6>
                        <ul className="document-list list-unstyled">
                          {section.items
                            .filter((x) => x.def.requirementLevel === 'required')
                            .map(({ effectiveCategoryAlias, state }) =>
                              renderDocumentItem(effectiveCategoryAlias, state)
                            )}
                        </ul>
                      </>
                    )}

                    {hasOptionalDocs(section) &&
                      (sectionHasRequiredDocs(section) ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-link btn-sm px-0 mb-2"
                            onClick={() =>
                              setOptionalOpen((o) => ({
                                ...o,
                                [section.key]: !o[section.key],
                              }))
                            }
                          >
                            {optionalOpen[section.key] ? 'Ocultar opcionales' : 'Ver opcionales'}
                          </button>
                          {optionalOpen[section.key] && (
                            <ul className="document-list list-unstyled">
                              {section.items
                                .filter((x) => x.def.requirementLevel === 'optional')
                                .map(({ effectiveCategoryAlias, state }) =>
                                  renderDocumentItem(effectiveCategoryAlias, state)
                                )}
                            </ul>
                          )}
                        </>
                      ) : (
                        <>
                          <h6 className="text-muted small text-uppercase mb-2">Opcionales</h6>
                          <ul className="document-list list-unstyled">
                            {section.items
                              .filter((x) => x.def.requirementLevel === 'optional')
                              .map(({ effectiveCategoryAlias, state }) =>
                                renderDocumentItem(effectiveCategoryAlias, state)
                              )}
                          </ul>
                        </>
                      ))}
                  </>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>

        <div className="col-md-4">
          <div className="qr-section text-center">
            <div className="card">
              <div className="card-body p-1">
                <FontAwesomeIcon icon={faMobileScreen} className="display-4 text-primary mb-3" />
                <h5>¿Prefieres usar el celular?</h5>
                <p className="text-muted small mb-4">
                  Escanea el código QR para subir los documentos desde tu teléfono
                </p>

                {qrUrl && (
                  <div className="qr-container mb-3">
                    <canvas id="qr-canvas" className="mb-3"></canvas>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleShareMobileAccess}
                    >
                      <i className="bi bi-share me-2"></i>
                      Compartir acceso
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isUploading && (
        <>
          <div className="progress mt-3">
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${progress}%` }}
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {progress}%
            </div>
          </div>
          <span>Subiendo documentos...</span>
        </>
      )}

      {errorMessage && (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {errorMessage}
        </div>
      )}

      <div className="d-flex justify-content-end align-items-center mt-4">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Subiendo...
            </>
          ) : (
            <>
              Continuar
              <i className="bi bi-arrow-right ms-2"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentUploadStep;
